<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { ToastPortal, ToastProvider, ToastViewport } from 'reka-ui';
import Toast from './toast.vue';
import { useToast } from './use-toast';
import {
  TOAST_DEFAULT_DURATION,
  TOAST_MAX_VISIBLE,
  TOAST_STACK_GAP,
  TOAST_STACK_SCALE_STEP,
} from './toast.constants';
import type { Toast as ToastItem } from './toast.types';

const { toasts } = useToast();

const shown = computed(() => toasts.value.slice(-TOAST_MAX_VISIBLE));

const hovered = ref(false);
const items = useTemplateRef('items');

const heightOf = (toast: ToastItem): number =>
  items.value?.find((item) => item?.id === toast.id)?.height ?? 0;

const offsetOf = (index: number): number =>
  shown.value
    .slice(index + 1)
    .reduce(
      (total: number, toast: ToastItem) =>
        total + heightOf(toast) + TOAST_STACK_GAP,
      0,
    );

const totalHeight = computed(() => offsetOf(-1));
const frontHeight = computed(() => {
  const front = shown.value.at(-1);
  return front ? heightOf(front) : 0;
});

const isFront = (toast: ToastItem): boolean =>
  !hovered.value && toast === shown.value.at(-1);

const pulsing = (toast: ToastItem): 'odd' | 'even' | undefined => {
  if (!toast.duplicate) return undefined;
  return toast.duplicate % 2 === 0 ? 'even' : 'odd';
};

const stackStyle = (toast: ToastItem): Record<string, string> => {
  const index = shown.value.indexOf(toast);
  if (index === -1) return {};
  const before = shown.value.length - 1 - index;
  return {
    '--index': `${index}`,
    '--before': `${before}`,
    '--offset': `${offsetOf(index)}`,
    '--height': `${heightOf(toast)}`,
    '--scale': hovered.value
      ? '1'
      : `calc(1 - var(--before) * ${TOAST_STACK_SCALE_STEP})`,
    '--translate': hovered.value
      ? 'calc(var(--offset) * 1px)'
      : `calc(var(--before) * ${TOAST_STACK_GAP}px)`,
    '--transform': 'translateY(var(--translate)) scale(var(--scale))',
  };
};
</script>

<template>
  <ToastProvider
    :duration="TOAST_DEFAULT_DURATION"
    swipe-direction="right"
    label="Notification"
  >
    <Toast
      v-for="toast in toasts"
      :key="toast.id"
      ref="items"
      :toast="toast"
      :data-visible="shown.includes(toast)"
      :data-expanded="hovered"
      :data-front="isFront(toast)"
      :data-pulsing="pulsing(toast)"
      :style="stackStyle(toast)"
      class="pointer-events-auto absolute inset-x-0 top-0 z-(--index) transform-(--transform) transition-[transform,translate,height] duration-200 ease-out data-[swipe=move]:transition-none data-[visible=false]:pointer-events-none data-[visible=false]:invisible data-[visible=true]:data-[expanded=false]:data-[front=false]:h-(--front-height) data-[visible=true]:data-[expanded=false]:data-[front=false]:[&>*>*]:opacity-0 data-[front=false]:[&>*>*]:transition-opacity data-[front=false]:[&>*>*]:duration-100 data-[state=open]:animate-toast-in data-[state=closed]:animate-toast-out data-[state=closed]:data-[expanded=false]:data-[front=false]:animate-toast-collapsed-out data-[state=open]:data-[pulsing=odd]:animate-toast-pulse-a data-[state=open]:data-[pulsing=even]:animate-toast-pulse-b data-[swipe=move]:translate-x-(--reka-toast-swipe-move-x) data-[swipe=end]:translate-x-(--reka-toast-swipe-end-x) data-[swipe=cancel]:translate-x-0 data-[swipe=end]:animate-toast-swipe-out"
    />
    <ToastPortal>
      <ToastViewport
        :data-expanded="hovered"
        class="fixed top-lg right-lg left-lg z-100000 flex flex-col sm:left-auto sm:w-[400px] data-[expanded=false]:h-(--front-height) data-[expanded=true]:h-(--height)"
        :style="{
          '--front-height': `${frontHeight}px`,
          '--height': `${totalHeight}px`,
        }"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
      />
    </ToastPortal>
  </ToastProvider>
</template>
